import React, { useRef } from 'react';
import { FieldArray } from 'formik';
import { FaCalculator, FaCheckDouble } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import ReusableModal from './ReuseableModal';
import CustomButton from './CustomButton';
import CustomTable from './CustomTable';
import TextInput from './TextInput';

const CostManagementModal = ({
  isVisible,
  onClose,
  selectedItemIndex,
  setFieldValue,
  formValues,
  onSave
}) => {
  const { t } = useTranslation();
  const formRef = useRef(null);

  const costs = formValues?.items?.[selectedItemIndex]?.manage_costs || [];

  const handleSave = () => {
    const currentCosts = formRef.current?.values?.items?.[selectedItemIndex]?.manage_costs
      || formValues?.items?.[selectedItemIndex]?.manage_costs
      || [];

    const totalManageCosts = currentCosts.reduce((sum, cost) => {
      const price = Number(cost.price);
      if (isNaN(price) || price < 0) return sum;
      return sum + price;
    }, 0);

    if (currentCosts.length > 0 && totalManageCosts <= 0) {
      toast.error(t('Total cost must be greater than 0'));
      return;
    }

    if (onSave) {
      onSave(totalManageCosts);
    }

    onClose();
  };

  const tableColumns = [
    {
      field: 'type',
      header: t('Type'),
      body: (rowData) => (
        <div className="input-field !bg-gray-100 px-3 py-2 rounded">
          {rowData?.type || "-"}
        </div>
      ),
      style: { width: '150px' }
    },
    {
      field: 'code',
      header: t('Code'),
      body: (rowData) => (
        <div className="input-field !bg-gray-100 px-3 py-2 rounded">
          {rowData?.code || "-"}
        </div>
      ),
      style: { width: '150px' }
    },
    {
      field: 'price',
      header: t('Price'),
      body: (rowData, { rowIndex }) => (
        <TextInput
          name={`items[${selectedItemIndex}].manage_costs[${rowIndex}].price`}
          type="number"
          step="any"
          placeholder={t("Enter price")}
          hideErrorText={true}
          useFormik={true}
        />
      ),
      style: { width: '90px' }
    },
    {
      field: 'description',
      header: t('Description'),
      body: (rowData) => (
        <div className="input-field !bg-gray-100 px-3 py-2 rounded">
          {rowData?.description || "-"}
        </div>
      ),
      style: { width: '150px' }
    }
  ];

  return (
    <ReusableModal
    position='top'
      isVisible={isVisible}
      onClose={onClose}
      title={t("Cost Management")}
      HeaderIcon={FaCalculator}
      className="w-full md:w-[800px] lg:w-[900px]"
      footerButtons={
        <div className="flex items-center gap-2">
          <CustomButton
            label={t("Save")}
            onClick={handleSave}
            buttonType="primary"
            icon={FaCheckDouble}
          />
        </div>
      }
    >
      <div className="">
        <FieldArray name={`items[${selectedItemIndex}].manage_costs`}>
          {({ form }) => {
            // Store form in ref when it renders to access current values
            if (form && formRef.current !== form) {
              formRef.current = form;
            }
            return (
              <CustomTable
                data={form?.values?.items?.[selectedItemIndex]?.manage_costs || costs}
                columns={tableColumns}
                isLoading={false}
                emptyMessage="No cost data available"
                tableStyle={{ minWidth: '100%' }}
                rootyStyles="overflow-auto border border-gray-200 rounded custom-table"
              />
            );
          }}
        </FieldArray>
      </div>
    </ReusableModal>
  );
};

export default CostManagementModal;